import React, { useState, useEffect } from 'react';
import airportService, { Airport, CreateAirportRequest, UpdateAirportRequest, PaginatedResponse } from '../services/airportService';
import AirportModal from './AirportModal';
import './AirportManagement.css';

interface AirportManagementProps {
  onClose?: () => void;
}

const AirportManagement: React.FC<AirportManagementProps> = ({ onClose }) => {
  const [airports, setAirports] = useState<Airport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingAirport, setEditingAirport] = useState<Airport | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC');
  const [selectedAirports, setSelectedAirports] = useState<number[]>([]);

  const sortOptions = [
    { value: 'id', label: 'ID' },
    { value: 'code', label: 'Код' },
    { value: 'name', label: 'Название' },
    { value: 'displayName', label: 'Отображаемое название' },
    { value: 'createdAt', label: 'Дата создания' }
  ];

  useEffect(() => {
    fetchAirports();
  }, [currentPage, pageSize, sortBy, sortDir]);

  const fetchAirports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: PaginatedResponse<Airport> = await airportService.getAirports(currentPage, pageSize, sortBy, sortDir);
      setAirports(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      console.error('Error fetching airports:', err);
      setError('Ошибка при загрузке аэропортов');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAirport = async (data: CreateAirportRequest) => {
    try {
      await airportService.createAirport(data);
      setIsCreateModalOpen(false);
      fetchAirports();
      alert('Аэропорт успешно создан!');
    } catch (err: any) {
      console.error('Error creating airport:', err);
      alert(err.message || 'Ошибка при создании аэропорта');
    }
  };

  const handleUpdateAirport = async (id: number, data: UpdateAirportRequest) => {
    try {
      await airportService.updateAirport(id, data);
      setEditingAirport(null);
      fetchAirports();
      alert('Аэропорт успешно обновлен!');
    } catch (err: any) {
      console.error('Error updating airport:', err);
      alert(err.message || 'Ошибка при обновлении аэропорта');
    }
  };

  const handleDeleteAirport = async (airport: Airport) => {
    if (!window.confirm(`Вы уверены, что хотите удалить аэропорт "${airport.displayName}"?`)) {
      return;
    }

    try {
      await airportService.deleteAirport(airport.id);
      fetchAirports();
      alert('Аэропорт успешно удален!');
    } catch (err: any) {
      console.error('Error deleting airport:', err);
      alert(err.message || 'Ошибка при удалении аэропорта');
    }
  };



  const handleBulkAction = async (action: 'activate' | 'delete') => {
    if (selectedAirports.length === 0) {
      alert('Выберите аэропорты для выполнения действия');
      return;
    }

   

    try {
      for (const airportId of selectedAirports) {
        switch (action) {
          case 'activate':
            await airportService.toggleAirport(airportId);
            break;
          case 'delete':
            await airportService.deleteAirport(airportId);
            break;
        }
      }
      setSelectedAirports([]);
      fetchAirports();
      alert(`Действие выполнено для ${selectedAirports.length} аэропортов`);
    } catch (err) {
      console.error('Error performing bulk action:', err);
      alert('Ошибка при выполнении действия');
    }
  };

  const handleSelectAirport = (airportId: number) => {
    setSelectedAirports(prev => 
      prev.includes(airportId) 
        ? prev.filter(id => id !== airportId)
        : [...prev, airportId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAirports.length === filteredAirports.length) {
      setSelectedAirports([]);
    } else {
      setSelectedAirports(filteredAirports.map(airport => airport.id));
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortDir('ASC');
    }
  };

  const openEditModal = (airport: Airport) => {
    setEditingAirport(airport);
  };

  const closeEditModal = () => {
    setEditingAirport(null);
  };

  const filteredAirports = airports.filter(airport => {
    const matchesSearch = 
      airport.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      airport.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      airport.displayName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = !showActiveOnly || airport.isActive;
    return matchesSearch && matchesActive;
  });

  if (loading) {
    return (
      <div className="airport-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка аэропортов...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="airport-management">
      <div className="airport-header">
        <div className="header-left">
          <h2>Управление аэропортами</h2>
          <div className="quick-stats">
            <span className="stat-item">
              <strong>{totalElements}</strong> всего
            </span>
            <span className="stat-item">
              <strong>{airports.filter(a => a.isActive).length}</strong> активных
            </span>
            <span className="stat-item">
              <strong>{airports.filter(a => !a.isActive).length}</strong> неактивных
            </span>
          </div>
        </div>
        <div className="airport-actions">
          <button 
            onClick={() => setIsCreateModalOpen(true)} 
            className="btn btn-primary"
          >
            Создать аэропорт
          </button>
          <div className="bulk-actions">

            <button onClick={() => handleBulkAction('delete')} className="bulk-delete-btn">
              Удалить выбранных
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchAirports} className="retry-button">
            Попробовать снова
          </button>
        </div>
      )}

      <div className="airport-filters">
        <div className="search-section">
          <input
            type="text"
            placeholder="Поиск по коду, названию..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-section">
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
            />
            Только активные
          </label>
        </div>
        <div className="view-controls">
          <button onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')} className="view-toggle-btn">
            {viewMode === 'cards' ? 'Список' : 'Карточки'}
          </button>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="page-size-select"
          >
            <option value={5}>5 на странице</option>
            <option value={10}>10 на странице</option>
            <option value={20}>20 на странице</option>
            <option value={50}>50 на странице</option>
          </select>
        </div>
      </div>

      <div className="airport-list">
        {viewMode === 'cards' ? (
          filteredAirports.map((airport) => (
            <div key={airport.id} className="airport-card">
              <div className="airport-info">
                <div className="airport-header-info">
                  <div className="airport-name">{airport.displayName}</div>
                  <span 
                    className={`airport-status-badge ${airport.isActive ? 'active' : 'inactive'}`}
                  >
                    {airport.isActive ? 'Активен' : 'Неактивен'}
                  </span>
                </div>
                <div className="airport-details">
                  <p><strong>Код:</strong> {airport.code}</p>
                  <p><strong>Название:</strong> {airport.name}</p>
                  <p><strong>Отображаемое название:</strong> {airport.displayName}</p>
                </div>
                <div className="airport-meta">
                  <span>Создан: {airport.createdAt ? new Date(airport.createdAt).toLocaleDateString('ru-RU') : 'Не указано'}</span>
                </div>
              </div>
              <div className="airport-actions">
                <button 
                  onClick={() => openEditModal(airport)} 
                  className="btn btn-primary"
                >
                  Редактировать
                </button>

                <button 
                  onClick={() => handleDeleteAirport(airport)} 
                  className="btn btn-danger"
                >
                  Удалить
                </button>
              </div>
            </div>
          ))
        ) : (
          <table className="airport-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedAirports.length === filteredAirports.length && filteredAirports.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th onClick={() => handleSort('id')} className="sortable">
                  ID {sortBy === 'id' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('code')} className="sortable">
                  Код {sortBy === 'code' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('name')} className="sortable">
                  Название {sortBy === 'name' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('displayName')} className="sortable">
                  Отображаемое название {sortBy === 'displayName' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredAirports.map((airport) => (
                <tr key={airport.id} className="airport-row">
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedAirports.includes(airport.id)}
                      onChange={() => handleSelectAirport(airport.id)}
                    />
                  </td>
                  <td>{airport.id}</td>
                  <td>{airport.code}</td>
                  <td>{airport.name}</td>
                  <td>{airport.displayName}</td>
                  <td>
                    <span 
                      className={`airport-status-badge ${airport.isActive ? 'active' : 'inactive'}`}
                    >
                      {airport.isActive ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td>
                    <button 
                      onClick={() => openEditModal(airport)} 
                      className="btn btn-primary"
                    >
                      Редактировать
                    </button>

                    <button 
                      onClick={() => handleDeleteAirport(airport)} 
                      className="btn btn-danger"
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filteredAirports.length === 0 && !loading && (
        <div className="empty-state">
          <p>Аэропорты не найдены</p>
        </div>
      )}

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <button onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0}>Назад</button>
          <span>Страница {currentPage + 1} из {totalPages}</span>
          <button onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1}>Вперед</button>
        </div>
      )}

      {/* Модальное окно создания */}
      {isCreateModalOpen && (
        <AirportModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            fetchAirports();
          }}
          airport={null}
          isEdit={false}
        />
      )}

      {/* Модальное окно редактирования */}
      {editingAirport && (
        <AirportModal
          isOpen={!!editingAirport}
          onClose={closeEditModal}
          onSuccess={() => {
            closeEditModal();
            fetchAirports();
          }}
          airport={editingAirport}
          isEdit={true}
        />
      )}
    </div>
  );
};

export default AirportManagement;
